import { LanguagesType } from '../locale/localizationContext';
import { Routes } from '../routes/types';
import citizenships from '../assets/citizenships.json';

export enum WeekDay {
  Mon = 'monday',
  Tue = 'tuesday',
  Wed = 'wednesday',
  Thu = 'thursday',
  Fri = 'friday',
  Sat = 'saturday',
  Sun = 'sunday',
}

export enum FaqFeature {
  Reception = 'reception',
  Login = 'login',
  PaymentMethods = 'payment-methods',
  Pdr = 'pdr',
  Address = 'edit-address',
  TimePicker = 'time-picker',
  Order = 'order',
}

export enum DeliveryContentType {
  Return = 'Return',
  Partial = 'Partial',
  Full = 'Full',
}

export enum DeliveryType {
  Delivery = 'Delivery',
  Collect = 'Collect',
  PickUpAndDropOff = 'Pickupanddropoff',
}

export enum PremiseType {
  Reception = 'CONCIERGE',
  Office = 'OFFICE',
  Store = 'STORE',
}

export enum AddressType {
  Private = 'INDIVIDUAL',
  Company = 'COMPANY',
}

export enum Product {
  Pdr = 'PDR',
  Appointment = 'RESCHEDULE',
}

export enum MlkAction {
  Appointment = 'appointment',
  Pdr = 'pdr',
  Reception = 'reception',
  EditAddress = 'edit-address',
  EditNotes = 'edit-notes',
  EditDoorbellPosition = 'edit-doorbell-position',
  Review = 'review',
  EditReview = 'edit-review',
}

export enum OrderState {
  Blocked = 'blocked',
  Failed = 'failed',
  Baseline = 'baselineReady',
  Unassigned = 'unassigned',
  Loading = 'loading',
  Loaded = 'loaded',
  Committed = 'committed',
  Deleted = 'deleted',
  ToReschedule = 'toReschedule',
  Fulfilled = 'fulfilled',
  InTransit = 'inTransit',
}

export enum CalculatedOrderStatus {
  Missed = 'missed',
  Suspended = 'suspended',
  Failed = 'failed',
  Blocked = 'blocked',
  Scheduled = 'scheduled',
  ScheduledReception = 'scheduled_reception',
  StandBy = 'stand_by',
  Masked = 'masked',
  ProtectionMode = 'protection_mode',
  TooSoon = 'too_soon',
  Arriving = 'arriving',
  InTransit = 'in_transit',
  Fulfilled = 'fulfilled',
  ToReschedule = 'to_reschedule',
  GeocodingKo = 'geocoding_ko',
  Deleted = 'deleted',
}

export enum AttemptState {
  First = 1,
  Second = 2,
  Third = 3,
  Fourth = 4,
  Stock = 5,
  Done = 6,
}

export enum DriverState {
  Arraving,
  BubbleIn,
  Arrived,
}

export enum Delay {
  No = 'no',
  Slight = 'slight',
  Major = 'major',
}

export type Vote = 1 | 2 | 3 | 4 | 5;

export enum ShippedProductPaymentMethod {
  /**
   * Credit card
   */
  Cc = 'cc',

  /**
   * Debit card
   */
  Db = 'db',

  /**
   * Bank transfer
   */
  Bt = 'bt',

  /**
   * Cash on delivery
   */
  C1 = 'c1',

  /**
   * Cash on delivery, cheque on delivery
   */
  C2 = 'c2',

  /**
   * Cash on delivery, cashier's check on delivery
   */
  C3 = 'c3',

  /**
   * Cash on delivery, cheque to the sender
   */
  C4 = 'c4',

  /**
   * Cash on delivery, cashier's check to the sender
   */
  C5 = 'c5',

  /**
   * Cash on delivery, cheque to the original sender
   */
  C6 = 'c6',

  /**
   * Cash cashier's check to the original sender
   */
  C7 = 'c7',

  /**
   * PayPal
   */
  Pp = 'pp',

  /**
   * Gift Card
   */
  Gf = 'gf',

  /**
   * Other
   */
  C99 = '99',
}

export enum BlockedReason {
  /**
   * Exceeded delivery attempts
   */
  B01 = 'b01',

  /**
   * Wrong address (blocked by shipper configurator)
   */
  B02 = 'b02',

  /**
   * Wrong address
   */
  B03 = 'b03',

  /**
   * Wrong doorbell
   */
  B04 = 'b04',

  /**
   * Recipient refused
   */
  B05 = 'b05',

  /**
   * Damaged goods
   */
  B06 = 'b06',
}

export enum Problem {
  /**
   * Recipient absent
   */
  F01 = 'f01',

  /**
   * Gatehouse closed
   */
  F02 = 'f02',

  /**
   * Missing intercom name
   */
  F03 = 'f03',

  /**
   * House number not found
   */
  F04 = 'f04',

  /**
   * Rejected by recipient
   */
  F05 = 'f05',

  /**
   * Cash on delivery not paid
   */
  F06 = 'f06',

  /**
   * Exceptional circumstances
   */
  F07 = 'f07',

  /**
   * Delivery not made due to affixable reserve
   */
  F08 = 'f08',

  /**
   * Wrong navigation / wrong address
   */
  F09 = 'f09',

  /**
   * Packages not found
   */
  F10 = 'f10',

  /**
   * Damaged package
   */
  F11 = 'f11',

  /**
   * Cause specified by the driver
   */
  F99 = 'f99',
}

export enum RegistrationType {
  Light,
  Strong,
  None,
}

export enum OperationState {
  Completed,
  NotCompleted,
}

export enum OperationType {
  AddressChange = 'address-change',
  Pdr = 'pdr',
  Reschedule = 'reschedule',
  OpeningHours = 'opening-hours',
  AddNotes = 'add-notes',
  FreeReschedule = 'free-reschedule',
}

type ArrayElement<ArrayType extends readonly unknown[]> =
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

export type Citizeship = ArrayElement<typeof citizenships>;

export type BaseOperation<T extends OperationType> = {
  operationType: T;
  operationState: OperationState;
  routing: {
    url: Routes;
    state: any;
  };
  registrationType: RegistrationType;
};

export type ChangeAddressOperation =
  BaseOperation<OperationType.AddressChange> & {
    savedData: any;
  };

export type PdrOperation = BaseOperation<OperationType.Pdr> & {
  savedData: any;
};

export type RescheduleOperation = BaseOperation<OperationType.Reschedule> & {
  savedData: any;
};

export type FreeRescheduleOperation =
  BaseOperation<OperationType.FreeReschedule> & {
    savedData: any;
  };

export type OpeningHoursOperation =
  BaseOperation<OperationType.OpeningHours> & {
    savedData: any;
  };

export type AddNotesOperation = BaseOperation<OperationType.AddNotes> & {
  savedData: any;
};

export type Operation =
  | ChangeAddressOperation
  | PdrOperation
  | RescheduleOperation
  | OpeningHoursOperation
  | AddNotesOperation
  | FreeRescheduleOperation;

type PosTerritoriesCmsConfig = {
  id: number;
  territoryName: string | null;
  territoryId: number;
};

export type ShippersConfigInfo = { id: number; shipperId: number };
export interface CmsConfigs {
  id: number;
  receptionMinimumHoursPerDay: number;
  receptionMinimumDaysPerWeek: number;
  addressConvergenceRadiusInMeters: number;
  published_at: string;
  created_at: string;
  updated_at: string;
  timePickerPredefinedSelections?: TimePickerPreset[];
  pickupPointDefaultPrice: number;
  onlyFreeAppointments: boolean;
  featureFlags: FeatureFlags;
  paymentMethods: PaymentMethods;
  pdrPreviousDays: number;
  posCashOnDeliveryTerritories: Array<PosTerritoriesCmsConfig>;
  shippersForSameDayPDR: Array<ShippersConfigInfo>;
  shippersForPreviousDaysPDR: Array<ShippersConfigInfo>;
  shippersWithHiddenSignature: Array<ShippersConfigInfo>;
  posteReviewModalSuccessfulSurveyId: string;
  posteReviewModalFailedSurveyId: string;
  appName: string;
}

export type FeatureFlags = {
  isEditAddressEnabled: boolean;
  isEditDoorbellPositionEnabled: boolean;
  isEditNotesEnabled: boolean;
  isAppointmentEnabled: boolean;
  isReviewEnabled: boolean;
  isPdrEnabled: boolean;
  isReceptionEnabled: boolean;
  useReviewModePoste: boolean;
};

export type PaymentMethods = {
  creditCard: boolean;
  postePay: boolean;
  payPal: boolean;
};

export interface TimePickerPreset {
  id: number;
  titleTranslationKey: string;
  timeRangeStart?: string | null;
  timeRangeEnd?: string | null;
  isCurrentDay?: boolean | null;
  descriptionTranslationKey: string;
}

export interface Helps {
  id: number;
  locale: LanguagesType;
  published_at: string;
  created_at: string;
  updated_at: string;
  timePickerHelpPages: FaqPage[];
  faqPages: FaqPage[];
  faqEditAddress: FaqPage[];
  faqPdr: FaqPage[];
  faqReception: FaqPage[];
  faqLogin: FaqPage[];
  faqPaymentMethods: FaqPage[];
  localizations: LanguagesType[];
}

export interface Article {
  id: number;
  title: string;
  description: string;
  content: string;
  published_at: string;
}

export interface PromoBanner {
  id: number;
  title: string;
  article: Article;
  description: string;
  image: Image;
  largeImage?: Image;
}
export interface PromoCookie {
  description: string;
  flatDiscount: number;
  id: number;
  image: Image;
  largeImage?: Image;
  percentDiscount: number;
  products: Array<Product>;
  promoType: 'flatDiscount';
  shippers: Array<{ id: number; mlkShipperId: string }>;
  territories: Array<{ id: number; mlkTerritoryId: string }>;
  title: string;
}

export interface OnlyContentModel {
  content: string;
}

export interface ContentHrefTargetModel extends OnlyContentModel {
  href?: string;
  target?: '_blank' | '_sefl';
}

export interface Image {
  id: number;
  name: string;
  alternativeText: string;
  caption: string;
  width: number;
  height: number;
  formats: Formats;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl?: null;
  provider: string;
  provider_metadata?: null;
  created_at: string;
  updated_at: string;
}
export interface Formats {
  thumbnail: Thumbnail;
}
export interface Thumbnail {
  ext: string;
  url: string;
  hash: string;
  mime: string;
  name: string;
  path?: null;
  size: number;
  width: number;
  height: number;
}

export interface HelpPage {
  id: number;
  title: string;
  content: string;
}

export interface FaqPage {
  id: number;
  title: string;
  description?: string;
  content: string;
}

export interface LocaleParams {
  locale: LanguagesType;
}

export interface ArticleDetailsRequest {
  locale: LanguagesType;
  newsId: string;
}
